"use client";

import * as React from "react";
import Link from "next/link";
import { ShieldCheck, Lock, ArrowLeft, X, AlertCircle } from "lucide-react";
import { fieldCls, Field, SectionLabel, errCls } from "@/components/signup/ui";
import { MONTHS } from "@/components/signup/signup-schema";
import type { SignupConfig } from "@/components/signup/signup-content";

type Plan = "monthly" | "yearly" | "standard" | "premium";
type Errors = Record<string, string>;

const AGREEMENTS: { key: "agreeTerms" | "agreePrivacy" | "agreeMembership" | "agreeSavings"; label: string; href: string }[] = [
  { key: "agreeTerms", label: "Terms of Service", href: "/trust/terms-conditions/" },
  { key: "agreePrivacy", label: "Privacy Policy", href: "/trust/privacy-policy/" },
  { key: "agreeMembership", label: "Membership Agreement", href: "/trust/membership-agreement/" },
  { key: "agreeSavings", label: "30 Days Money-back Guarantee Terms", href: "/trust/money-back-guarantee/" },
];

/**
 * Identity & Consent popup — the second step of the dashboard paywall. Collects
 * billing address, identity (DOB + SSN last 4) and the membership agreements,
 * then opens Stripe Checkout via /api/member/checkout. Premium is activated by
 * the Stripe webhook; the member returns to the (now unlocked) dashboard.
 */
export function IdentityConsentModal({
  config,
  plan,
  variant,
  memberName,
  onBack,
  onClose,
}: {
  config: SignupConfig;
  plan: Plan;
  variant: "residential" | "business";
  memberName: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const who = memberName.trim();
  const years = React.useMemo(() => {
    const newest = new Date().getFullYear() - 18;
    return Array.from({ length: 100 }, (_, i) => newest - i);
  }, []);
  const days = React.useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  const [billingSame, setBillingSame] = React.useState(true);
  const [billing, setBilling] = React.useState({ street: "", city: "", state: "", zip: "" });
  const [dob, setDob] = React.useState({ day: "", month: "", year: "" });
  const [ssnLast4, setSsnLast4] = React.useState("");
  const [errors, setErrors] = React.useState<Errors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState("");

  function validate(): Errors {
    const e: Errors = {};
    if (!billingSame) {
      if (billing.street.trim().length < 1) e.billingStreet = "Required for a different billing address.";
      if (billing.city.trim().length < 1) e.billingCity = "Required for a different billing address.";
      if (billing.state.trim().length < 1) e.billingState = "Required for a different billing address.";
      if (!/^\d{5}$/.test(billing.zip.trim())) e.billingZip = "Enter a 5-digit ZIP code.";
    }
    const day = Number(dob.day), month = Number(dob.month), year = Number(dob.year);
    if (!dob.day || !dob.month || !dob.year) {
      e.dob = "Please enter your full date of birth.";
    } else {
      const dt = new Date(year, month - 1, day);
      const real = dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
      if (!real) {
        e.dob = "Please enter a valid date of birth.";
      } else {
        const now = new Date();
        let age = now.getFullYear() - year;
        if (now.getMonth() < month - 1 || (now.getMonth() === month - 1 && now.getDate() < day)) age--;
        if (age < 18) e.dob = "You must be at least 18 years old.";
      }
    }
    if (!/^\d{4}$/.test(ssnLast4.trim())) e.ssnLast4 = "Enter the last 4 digits of your SSN.";
    // Membership agreements are now accepted at signup (step 2), so they're not re-collected here.
    return e;
  }

  async function submit() {
    const e = validate();
    setErrors(e);
    setFormError("");
    if (Object.keys(e).length) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/member/checkout/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          variant,
          billingSame,
          billingStreet: billing.street,
          billingCity: billing.city,
          billingState: billing.state,
          billingZip: billing.zip,
          dobDay: dob.day,
          dobMonth: dob.month,
          dobYear: dob.year,
          ssnLast4,
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { checkoutUrl?: string; error?: string };
      if (!res.ok || !json.checkoutUrl) {
        setFormError(json.error || "We couldn't start checkout. Please try again.");
        setSubmitting(false);
        return;
      }
      window.location.href = json.checkoutUrl;
    } catch {
      setFormError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl my-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div className="flex items-start gap-3">
            <span className="hidden sm:flex w-12 h-12 shrink-0 rounded-full bg-leaf-100 items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-leaf-600" />
            </span>
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-cj-800 leading-tight">Complete Your Membership</h2>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                A few details to verify your identity and authorize your{" "}
                <span className="font-semibold text-navy-500">{config.plans.find((p) => p.value === plan)?.title ?? "Membership"} Plan</span>.
                Your card won&apos;t be charged until our team reviews your details.
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1 text-gray-400 hover:text-navy-500 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-7">
          {/* Billing address */}
          <div>
            <SectionLabel required>Billing Address</SectionLabel>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700">
                <input type="radio" name="cj-billing" className="w-4 h-4 accent-cj-800" checked={billingSame} onChange={() => setBillingSame(true)} />
                My billing address is the same as my service address.
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700">
                <input type="radio" name="cj-billing" className="w-4 h-4 accent-cj-800" checked={!billingSame} onChange={() => setBillingSame(false)} />
                My billing address is different from my service address.
              </label>
            </div>

            {!billingSame && (
              <div className="mt-4 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Billing Street" required error={errors.billingStreet} htmlFor="cj-bstreet">
                    <input id="cj-bstreet" className={fieldCls} value={billing.street} onChange={(e) => setBilling((b) => ({ ...b, street: e.target.value }))} />
                  </Field>
                  <Field label="Billing City" required error={errors.billingCity} htmlFor="cj-bcity">
                    <input id="cj-bcity" className={fieldCls} value={billing.city} onChange={(e) => setBilling((b) => ({ ...b, city: e.target.value }))} />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Billing State" required error={errors.billingState} htmlFor="cj-bstate">
                    <input id="cj-bstate" className={fieldCls} value={billing.state} onChange={(e) => setBilling((b) => ({ ...b, state: e.target.value }))} />
                  </Field>
                  <Field label="Billing Zip" required error={errors.billingZip} htmlFor="cj-bzip">
                    <input id="cj-bzip" inputMode="numeric" className={fieldCls} value={billing.zip} onChange={(e) => setBilling((b) => ({ ...b, zip: e.target.value.replace(/[^\d-]/g, "") }))} />
                  </Field>
                </div>
              </div>
            )}
          </div>

          {/* Identity */}
          <div>
            <SectionLabel required>Identity Information</SectionLabel>
            <div className="flex items-start gap-3 rounded-xl bg-cj-50 border border-cj-100 p-3.5 mb-4">
              <Lock className="w-5 h-5 text-cj-800 shrink-0 mt-0.5" />
              <div className="text-[13px] text-gray-600 leading-relaxed">
                <span className="font-bold text-cj-800">Secure Identity Verification.</span> Providers require identity
                verification before we can review rates, account details, or assist with switching services. Your information
                is encrypted and only used when required by participating providers.
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Date of Birth</label>
                <div className="grid grid-cols-3 gap-2">
                  <select className={fieldCls} value={dob.day} onChange={(e) => setDob((d) => ({ ...d, day: e.target.value }))}>
                    <option value="">Day</option>
                    {days.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select className={fieldCls} value={dob.month} onChange={(e) => setDob((d) => ({ ...d, month: e.target.value }))}>
                    <option value="">Month</option>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select className={fieldCls} value={dob.year} onChange={(e) => setDob((d) => ({ ...d, year: e.target.value }))}>
                    <option value="">Year</option>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {errors.dob && <p className={errCls}>{errors.dob}</p>}
              </div>
              <Field label="SSN (Last 4 digits)" required error={errors.ssnLast4} htmlFor="cj-ssn">
                <input
                  id="cj-ssn"
                  inputMode="numeric"
                  maxLength={4}
                  className={fieldCls}
                  placeholder="Last 4 digits of SSN"
                  value={ssnLast4}
                  onChange={(e) => setSsnLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                />
              </Field>
            </div>
          </div>

          {/* Membership agreements are accepted at signup (step 2) — referenced here, not re-collected. */}
          <p className="text-[12px] text-gray-400 leading-relaxed">
            By continuing you confirm the{" "}
            <Link href="/trust/terms-conditions/" target="_blank" className="underline hover:text-cj-800">Terms of Service</Link>,{" "}
            <Link href="/trust/privacy-policy/" target="_blank" className="underline hover:text-cj-800">Privacy Policy</Link>,{" "}
            <Link href="/trust/membership-agreement/" target="_blank" className="underline hover:text-cj-800">Membership Agreement</Link> and{" "}
            <Link href="/trust/money-back-guarantee/" target="_blank" className="underline hover:text-cj-800">30 Days Money-back Guarantee Terms</Link>{" "}
            you accepted when you created your account.
          </p>

          {/* What happens next */}
          <div className="rounded-xl bg-lavender-50 border border-lavender-200 p-4">
            <p className="text-[15px] font-bold text-cj-800 mb-4">What Happens Next?</p>
            <ol className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {config.whatHappensNext.map((s, i) => (
                <li key={s.title} className="flex flex-col items-center text-center">
                  <span className="relative w-10 h-10 rounded-full bg-leaf-100 flex items-center justify-center mb-2">
                    <s.icon className="w-5 h-5 text-leaf-600" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-cj-800 text-white text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </span>
                  <span className="text-[12px] font-bold text-navy-500 leading-tight">{s.title}</span>
                  <span className="text-[11px] text-gray-500 leading-tight mt-1">{s.desc}</span>
                </li>
              ))}
            </ol>
          </div>

          {formError && (
            <div className="flex items-start gap-2.5 rounded-lg bg-red-50 border border-red-200 p-3 text-[13px] text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {formError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-1.5 h-12 px-6 rounded-lg border border-lavender-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Plans
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-lg bg-leaf-500 hover:bg-leaf-600 text-white text-sm font-bold transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Lock className="w-4 h-4" />
              {submitting ? "Redirecting to checkout…" : "Continue to Secure Checkout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
